import { useRouterState } from "@tanstack/react-router"
import { ColumnSoloContext, useColumnLayout, type ColumnSpec } from "@/lib/column-layout"
import { ResizeHandle, useResizableWidth } from "@/components/ui/resizable"
import { useShellPrefs } from "@/lib/shell-prefs"

// Renders the responsive Main-content columns: measures its own width and shows
// the deepest active column always, adding shallower ones while they fit (else
// collapse). `focus` forces the deepest column only. The single visible column
// always flex-fills; otherwise `fill` columns flex and the rest are FIXED but
// DRAG-RESIZABLE — a splitter on the edge facing the fill column, width
// persisted per RESOURCE + column id (so each page keeps its own widths).
export function ColumnLayout({
  columns,
  focus,
}: {
  columns: ColumnSpec[]
  /** 1-based column number to render full-width (fullscreen that column). */
  focus?: number
}) {
  // "Open records full-width" preference: when on, force the deepest active
  // column (same as an explicit ?focus on the last one) so records never open
  // beside the list. An explicit ?focus still wins.
  const recordsFullscreen = useShellPrefs((s) => s.recordsFullscreen)
  const effFocus = focus ?? (recordsFullscreen && columns.length > 1 ? columns.length : undefined)
  const { containerRef, visible, width } = useColumnLayout(columns, { focus: effFocus })
  // Per-resource scope = the view segment of the path (/<org>/<event>/<view>/…),
  // stable as you drill deeper, so e.g. the People list width is independent of
  // the Travel list width.
  const scope = useRouterState({
    select: (s) => s.location.pathname.split("/").filter(Boolean)[2] ?? "main",
  })
  const only = visible.length === 1
  return (
    <div ref={containerRef} className="flex h-full w-full min-h-0 min-w-0 flex-1">
      {/* `only` → the visible column fills the Main area, so its TitleBar hides
          the (redundant) fullscreen button. */}
      <ColumnSoloContext.Provider value={only}>
        {visible.map((c, i) => {
        const fill = only || !!c.fill
        // A fixed column's splitter faces the nearest fill column: a leading
        // fixed column (i===0) grows from its RIGHT edge, a trailing one from
        // its LEFT. Max is container-aware so the fill sibling keeps its floor.
        const othersMin = visible.reduce((sum, o, j) => (j === i ? sum : sum + o.minWidth), 0)
        const max = width > 0 ? Math.max(c.minWidth, width - othersMin) : c.width ?? c.minWidth
        return (
          <Column
            key={c.id}
            spec={c}
            storageKey={`aaa2.col.${scope}.${c.id}`}
            fill={fill}
            edge={i === 0 ? "right" : "left"}
            max={max}
            border={i > 0}
          />
        )
        })}
      </ColumnSoloContext.Provider>
    </div>
  )
}

// One wrapper type for every column (fill or fixed) so toggling between the
// single-column (fill) and split (fixed) states never remounts the column's
// node — the list keeps its scroll/state when a detail opens beside it.
function Column({
  spec,
  storageKey,
  fill,
  edge,
  max,
  border,
}: {
  spec: ColumnSpec
  storageKey: string
  fill: boolean
  edge: "left" | "right"
  max: number
  border: boolean
}) {
  const { width, handleProps } = useResizableWidth({
    storageKey,
    defaultWidth: spec.width ?? spec.minWidth,
    min: spec.minWidth,
    max,
    edge,
  })
  // Stacked columns separate with a crisp left border (the soft drop shadow was
  // dropped — inner scroll containers clipped it unevenly).
  const borderCls = border ? "border-l border-plum-300/70 dark:border-white/15" : ""
  if (fill) {
    return <div className={`relative h-full min-h-0 min-w-0 flex-1 ${borderCls}`}>{spec.node}</div>
  }
  return (
    <div className={`relative z-10 h-full min-h-0 shrink-0 ${borderCls}`} style={{ width }}>
      {spec.node}
      <ResizeHandle {...handleProps} aria-label="Resize panel" />
    </div>
  )
}
